import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import type { Group, Item, Property, ValueType } from '@app/shared/models/common-models'
import { EntityService } from '@app/shared/services/entity.service'
import { PropertyChangeArgs } from '@app/shared/models/entity-update.model'
import { UntypedFormGroup } from '@angular/forms'

interface IPropertyRow {
  title: string
  properties: Record<string, Property>
  measurementUnitTextId: string
  dataType: ValueType
  items?: Item[]
}

@Component({
  selector: 'app-group-edit-table',
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- relies on pre-v22 default
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './group-edit-table.component.html',
  styleUrls: ['./group-edit-table.component.scss'],
  standalone: false,
})
export class GroupEditTableComponent implements OnChanges {
  @Input() tableTitle: string

  @Input() formGroup: UntypedFormGroup

  @Input() group: Group | Group[]

  @Input() columns: string[] | '*'

  @Input() showMeasurementUnit: boolean = true

  @Input() readonly: boolean = false

  @Output() propertyChange = new EventEmitter<PropertyChangeArgs>()

  public properties: IPropertyRow[] = []

  public tableColumns: string[] = []

  public constructor(private entityService: EntityService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.group || changes.columns) {
      this.tableColumns = ['title', ...this.columns]

      if (this.showMeasurementUnit) {
        this.tableColumns.push('measurementUnit')
      }

      const properties: IPropertyRow[] = []

      const [firstColumn, ...restColumns] = this.columns

      const allProperties: Property[] = []

      const groupsArray: Group[] = Array.isArray(this.group) ? this.group : [this.group]

      groupsArray.forEach(group => {
        allProperties.push(...group.Properties)
      })

      allProperties.forEach(property => {
        if ((property.Key.includes(firstColumn) || firstColumn === '*') && property.IsVisible) {
          const row: IPropertyRow = {
            title: property.TextId,
            properties: { [firstColumn]: property },
            measurementUnitTextId: property.MeasurementUnitTextId,
            dataType: property.ValueType,
            items: property.Items,
          }

          if (this.columns !== '*') {
            restColumns.forEach(column => {
              const columnPropertyKey = property.Key.replace(firstColumn, column)
              const columnProperty = allProperties.find(p => p.Key === columnPropertyKey)
              if (columnProperty) {
                row.properties[column] = columnProperty
              }
            })
          }

          properties.push(row)
        }
      })

      this.properties = properties
    }
  }

  public propertyChanged(property: Property, value: unknown): void {
    this.propertyChange.emit({
      Key: property.Key,
      Value: value,
      MeasurementUnitKey: property.MeasurementUnit,
    })
  }

  public trackByPropertyKey(i: number, row: IPropertyRow): string {
    return row.title
  }
}
